import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout,
} from 'components/FormLayout';
import { Buffer } from 'buffer';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import TLSProfileServerForm from 'routes/TlsProfile/components/TLSProfileServerForm';
import { Ref } from 'routes/TlsProfile/components/TLSProfileServerForm/TLSProfileServerForm';
import { RootState } from 'data/root.reducers';
import { showError } from 'shared/toaster';
import * as networkActions from 'data/network/network.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as distributorSelectors from 'data/distributor/distributor.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as tlsProxySelectors from 'data/tlsProxy/tlsProxy.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as nodesSelectors from 'data/nodes/nodes.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import * as certStatusSelectors from 'data/certStatus/certStatus.selectors';
import * as actions from 'data/accessFunction/accessFunction.actions';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as tlsProfileActions from 'data/tlsProfile/tlsProfile.actions';
import * as certStatusActions from 'data/certStatus/certStatus.actions';
import * as distributorActions from 'data/distributor/distributor.actions';
import * as securityInfoSelectors from 'data/securityInfo/securityInfo.selectors';
import { adaptToCreate, getCertStatusPayload } from 'data/tlsProfile/tlsProfile.adapter';
import * as tlsProxyActions from 'data/tlsProxy/tlsProxy.actions';
import { TlsProfile } from 'data/tlsProfile/tlsProfile.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { newTlsProfile } from 'routes/TlsProfile/TLSProfile.types';
import { WithAuditDetails } from 'data/types';
import { CertStatus } from 'data/certStatus/certStatus.types';
import { getCertStatusAuditDetailsToSubmit } from 'routes/TlsProfile/utils/certStatusDataToSubmit';
import { AuditActionType } from 'shared/userAudit/audit-constants';
import { SecurityInfo } from 'data/securityInfo/securityInfo.types';
import { tlsProfileTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptFormData,
  adaptTemplateOnFieldUpdate,
  adaptTemplateOptions,
  adaptTemplateOnCreate
} from '../../utils/tlsProfileTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/tlsProfileTemplateDataToSubmit';

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  tlsProfileTemplateDef: adaptTemplateVisibilityMode(
    tlsProfileTemplate,
    'create',
  ),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  distributorData: distributorSelectors.getDistributors(state),
  nodes: nodesSelectors.getNodes(state),
  tlsProfileTemplate: adaptTemplateVisibilityMode(tlsProfileTemplate, 'create'),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  sslCertDir: discoverySelectors.getSslCertDir(state),
  xccaCaNamePrefix: discoverySelectors.getXccaCertAuthorityNamePrefix(state),
  tlsProxies: tlsProxySelectors.getTlsProxyList(state),
  availableAccessFunction: supportedFeaturesSelectors.getAvailableAccessFunction(state),
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  enabledFeatures: supportedFeaturesSelectors.getEnabledFeatures(state),
  accessFunctionTemplates: templateSelectors.getEnabledAccessFunctionFormTemplatesByKey(state),
  allCertStatuses: certStatusSelectors.getAllCertStatus(state),
  securityInfos: securityInfoSelectors.getSecurityInfos(state),
});

const mapDispatch = {
  getDistributors: distributorActions.getDistributors,
  createTlsProfile: tlsProfileActions.createTlsProfile,
  goToTlsProfileList: tlsProfileActions.goToTlsProfileList,
  createCertStatus: certStatusActions.createCertStatus,
  createCertStatusAndGetAll: certStatusActions.createCertStatusAndGetAll,
  fetchTlsProxyListWithView: tlsProxyActions.fetchTlsProxyListWithView,
  getNetworks: networkActions.getNetworks,
  getAllCertStatus: certStatusActions.getAllCertStatus,
  getTemplates: warrantActions.getAllTemplates,
  getConfiguredAccessFunctions: actions.getConfiguredAccessFunctions,
  getAccessFunctions: actions.getAccessFunctionsWithViewOptions,
  getSupportedFeature: warrantActions.getWarrantFormConfig,
  getSecurityInfos: securityInfoActions.getSecurityInfos,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const TLSProfileCreate = ({
  history,
  userId,
  deliveryFunctionId,
  getNetworks,
  goToTlsProfileList,
  createTlsProfile,
  enabledAccessFunctions,
  availableAccessFunction,
  accessFunctionTemplates,
  userName,
  isAuditEnabled,
  tlsProfileTemplateDef,
  getDistributors,
  distributorData,
  allCertStatuses,
  getAllCertStatus,
  sslCertDir,
  xccaCaNamePrefix,
  getSupportedFeature,
  getAccessFunctions,
  getConfiguredAccessFunctions,
  enabledFeatures,
  securityInfos,
  getSecurityInfos,
  createCertStatusAndGetAll
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<TlsProfile>(
    cloneDeep(newTlsProfile),
  );
  const [template, setTemplate] = useState(tlsProfileTemplateDef);
  const [selectedTlsType, setSelectedTlsType] = useState('server');
  const [selectedAuthenType, setSelectedAuthenType] = useState('simple');
  const { t } = useTranslation();

  useEffect(() => {
    getNetworks(deliveryFunctionId);
    getDistributors(deliveryFunctionId);
    getAllCertStatus(deliveryFunctionId);
    getSupportedFeature(deliveryFunctionId);
    getAccessFunctions(deliveryFunctionId);
    getConfiguredAccessFunctions(deliveryFunctionId);
    getSecurityInfos(deliveryFunctionId);
  }, [deliveryFunctionId, getNetworks, getDistributors, getAllCertStatus, getSupportedFeature, getAccessFunctions, getConfiguredAccessFunctions, getSecurityInfos]);

  useEffect(() => {
    if (!template) {
      return;
    }
    let clonedTemplate = cloneDeep(template);
    clonedTemplate = adaptTemplateOnCreate(clonedTemplate);
    clonedTemplate = adaptTemplateOptions(clonedTemplate, enabledAccessFunctions, accessFunctionTemplates, undefined);
    setTemplate(clonedTemplate);
  }, [accessFunctionTemplates, enabledAccessFunctions]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as TlsProfile);
    let clonedTemplate = cloneDeep(template);

    let newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptTemplateOnFieldUpdate(
      clonedTemplate,
      fieldName,
      value,
      newData,
      selectedTlsType,
      selectedAuthenType,
      distributorData?.ss8Distributors,
      enabledAccessFunctions,
      accessFunctionTemplates,
      'create'
    );

    if (fieldName === 'tlsType') {
      setSelectedTlsType(value);
    }

    if (fieldName === 'authenType') {
      setSelectedAuthenType(value);
    }

    newData = adaptFormData(newData, clonedTemplate, fieldName, value);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateTLSActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const tlsProfileFormData = formRef.current.getValues();

    if (tlsProfileFormData.catype === 'XCCA') {
      const existingSecInfo = securityInfos.find((si) => si.catype === 'XCCA' && si.aftype === tlsProfileFormData.aftype && si.litype === tlsProfileFormData.litype);
      if (existingSecInfo) {
        const id = Buffer.from((existingSecInfo as SecurityInfo).id, 'base64').toString('binary');
        showError(`TLS Profile with ID=${id} already exists for AFTYPE=${tlsProfileFormData.aftype}, LITYPE=${tlsProfileFormData.litype}`);
        return;
      }
    }

    const activatedCert = (allCertStatuses ?? []).find((cs) => {
      return cs.AFTYPE === (formData.aftype ?? '') && cs.LITYPE === formData.litype && cs.STATUS === 'ACTIVATED'
    });

    if (formData.catype === 'XCCA') {
      if (activatedCert) {
        tlsProfileFormData.serialnumber = activatedCert?.SERIALNUMBER as string;
        tlsProfileFormData.privateKeyFile = `${activatedCert?.CERTID}-PK.pem`;
        tlsProfileFormData.certificateDirectory = `${sslCertDir}`;
        tlsProfileFormData.certificateFile = `${activatedCert?.CERTID}.cer`;
        tlsProfileFormData.certificateAuthority = `${xccaCaNamePrefix}-${getAlgorithmType(activatedCert.KEYALGORITHM)}.pem`;
      } else {
        tlsProfileFormData.serialnumber = 'NONE';
        tlsProfileFormData.privateKeyFile = 'NONE';
        tlsProfileFormData.certificateDirectory = 'NONE';
        tlsProfileFormData.certificateFile = 'NONE';
        tlsProfileFormData.certificateAuthority = 'NONE';
      }
      tlsProfileFormData.passphrase = 'NONE';
    }

    const payload = {
      data: adaptToCreate(tlsProfileFormData),
      auditDetails: getAuditDetailsToSubmit(
        tlsProfileFormData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };
    return payload;
  };

  const getAlgorithmType = (keyalgorithm: 'RSA_2048' | 'RSA_4096' |'RSA_8192' | 'ECDSA_256' | 'ECDSA_384' | 'ECDSA_521' | undefined) => {
    return keyalgorithm?.split('_')[0];
}

  const getCreateCertStatusPayload = (): WithAuditDetails<CertStatus> | undefined => {
    if (formRef.current === undefined || formRef.current === null) {
      return
    }

    if (!formRef.current.isValid(true, true)) {
      return;
    }

    const formData = formRef.current.getValues();
    if (formData.catype !== 'XCCA') {
      return;
    }

    const activeCertExists = (allCertStatuses ?? []).some(
      (cs) =>
        cs.AFTYPE === formData.aftype &&
        cs.LITYPE === formData.litype &&
        (cs.STATUS === 'ACTIVATED' || cs.STATUS === 'DOWNLOADED')
    );

    if (activeCertExists) {
      return;
    }

    const data = getCertStatusPayload(formData);

    const payload = {
      data,
      auditDetails: getCertStatusAuditDetailsToSubmit(
        data,
        null,
        AuditActionType.CERTSTATUS_ADD,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    return payload;
  }

  const handleClickSave = () => {
    const payload = getCreateTLSActionPayload();
    if (payload) {
      createTlsProfile(deliveryFunctionId, payload, history, getCreateCertStatusPayload());
    }
  };

  const handleCancel = () => goToTlsProfileList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <TLSProfileServerForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="TLSProfileCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('tlsProfile.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>{renderFormPanel('')}</FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(TLSProfileCreate));
